import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { useUnreadOpportunitiesContext } from '../contexts/UnreadOpportunitiesContext';
import { persistOpportunityRead, CRM_OPPORTUNITY_READ_ON_ACTIVITY_EVENT } from '../lib/crm/markOpportunityRead';
import {
  hasScheduledFollowUp,
  isLeadStaleNoActivity,
  loadCrmNoActivitySettings,
  type CrmNoActivitySettings,
} from '../lib/crm/noActivitySettings';
import {
  fetchLatestMessageActivityByCustomer,
  type RecentActivityContact,
} from '../lib/crm/recentMessageActivity';
import { buildCrmRequiredActions } from '../lib/crm/requiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';
import type { Customer } from '../lib/customers';
import type { CrmRequiredActionOpportunity } from '../lib/crm/requiredActions';

// Persisted caches (per business) so the Control Centre renders actions instantly —
// even on a cold app launch / page reload — with no layout jump.
const opportunitiesCache = createBusinessCache<CrmRequiredActionOpportunity[]>({
  persistKey: 'crm-opportunities',
});
const noActivitySettingsCache = createBusinessCache<CrmNoActivitySettings>({
  persistKey: 'crm-no-activity-settings',
});

/** Loads open-lead actions (new, follow-up due, no activity) for the control-centre inbox. */
export function useCrmRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const { markAsRead: updateUnreadBadge } = useUnreadOpportunitiesContext();
  const businessId = selectedBusiness?.id ?? '';

  const [opportunities, setOpportunities] = useState<CrmRequiredActionOpportunity[]>(
    () => opportunitiesCache.get(businessId) ?? []
  );
  const [noActivitySettings, setNoActivitySettings] = useState<CrmNoActivitySettings>(
    () => noActivitySettingsCache.get(businessId) ?? { enabled: false, hours: 48 }
  );
  const [now, setNow] = useState(() => new Date());
  const [isLoading, setIsLoading] = useState(() => !opportunitiesCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchOpportunities = useCallback(async (): Promise<{
    mapped: CrmRequiredActionOpportunity[];
    contacts: RecentActivityContact[];
  } | null> => {
    if (!businessId || !supabase) return null;

    // Only Open leads can ever become actions (see buildCrmRequiredActions), so filter
    // server-side — far fewer rows than pulling 500 of every status and filtering client-side.
    const { data: opportunitiesData, error: opportunitiesError } = await supabase
      .from('pipeline_opportunities')
      .select(
        'id, status, title, customer_id, created_at, is_unread, next_follow_up_at, last_activity_at, lead_score, lead_score_reason'
      )
      .eq('business_id', businessId)
      .eq('status', 'Open')
      .order('created_at', { ascending: false })
      .limit(500);

    if (opportunitiesError) throw opportunitiesError;

    if (!opportunitiesData?.length) {
      setOpportunities([]);
      opportunitiesCache.set(businessId, []);
      return { mapped: [], contacts: [] };
    }

    const customerIds = [
      ...new Set(opportunitiesData.map((opp) => opp.customer_id).filter(Boolean)),
    ];

    // phone/email feed the all_messages double-check for no-activity flags.
    const { data: customersData, error: customersError } =
      customerIds.length > 0
        ? await supabase
            .from('customers')
            .select('id, first_name, last_name, phone, email')
            .in('id', customerIds)
        : { data: [], error: null };

    if (customersError) throw customersError;

    const customerMap = new Map<string, Customer>();
    (customersData ?? []).forEach((customer) => {
      customerMap.set(customer.id, customer as Customer);
    });

    const mapped = opportunitiesData.map((opportunity) => {
      const customer = customerMap.get(opportunity.customer_id);
      const customerName = customer
        ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown Contact'
        : 'Unknown Contact';
      return { ...opportunity, customer_name: customerName };
    });
    setOpportunities(mapped);
    opportunitiesCache.set(businessId, mapped);
    return {
      mapped,
      contacts: (customersData ?? []).map((customer) => ({
        id: customer.id as string,
        phone: (customer as Customer).phone,
        email: (customer as Customer).email,
      })),
    };
  }, [businessId, supabase]);

  const fetchSettings = useCallback(async (): Promise<CrmNoActivitySettings | null> => {
    if (!businessId || !supabase) return null;
    try {
      const settings = await loadCrmNoActivitySettings(supabase, businessId);
      setNoActivitySettings(settings);
      noActivitySettingsCache.set(businessId, settings);
      return settings;
    } catch (err) {
      console.error('Error loading CRM no-activity settings:', err);
      return null;
    }
  }, [businessId, supabase]);

  // last_activity_at misses messages that never wrote an activity row (inbound
  // webhooks, automations, campaigns). Before trusting a "stale" flag, check
  // all_messages — the same source Messages renders — and lift the lead's last
  // activity to its latest real message. Only would-be-stale leads are checked,
  // so the extra query stays small.
  const reconcileWithMessages = useCallback(
    async (
      mapped: CrmRequiredActionOpportunity[],
      contacts: RecentActivityContact[],
      settings: CrmNoActivitySettings,
    ) => {
      if (!businessId || !supabase || !settings.enabled) return;

      const nowDate = new Date();
      const staleCandidates = mapped.filter(
        (opp) =>
          opp.status === 'Open' &&
          opp.customer_id &&
          !hasScheduledFollowUp(opp.next_follow_up_at, nowDate) &&
          isLeadStaleNoActivity(opp.last_activity_at, opp.created_at, settings.hours, nowDate),
      );
      if (staleCandidates.length === 0) return;

      const candidateCustomerIds = new Set(staleCandidates.map((opp) => opp.customer_id!));
      const candidateContacts = contacts.filter((c) => candidateCustomerIds.has(c.id));
      if (candidateContacts.length === 0) return;

      const sinceIso = new Date(
        nowDate.getTime() - settings.hours * 60 * 60 * 1000,
      ).toISOString();
      const latestByCustomer = await fetchLatestMessageActivityByCustomer(
        supabase,
        businessId,
        candidateContacts,
        sinceIso,
      );
      if (latestByCustomer.size === 0) return;

      setOpportunities((prev) => {
        const next = prev.map((opp) => {
          const latest = opp.customer_id ? latestByCustomer.get(opp.customer_id) : undefined;
          if (!latest) return opp;
          const currentMs = new Date(opp.last_activity_at || opp.created_at).getTime();
          const messageMs = new Date(latest).getTime();
          return Number.isFinite(messageMs) && messageMs > currentMs
            ? { ...opp, last_activity_at: latest }
            : opp;
        });
        opportunitiesCache.set(businessId, next);
        return next;
      });
    },
    [businessId, supabase],
  );

  const refreshData = useCallback(async () => {
    const [oppResult, settings] = await Promise.all([fetchOpportunities(), fetchSettings()]);
    if (oppResult && settings) {
      await reconcileWithMessages(oppResult.mapped, oppResult.contacts, settings);
    }
    setNow(new Date());
  }, [fetchOpportunities, fetchSettings, reconcileWithMessages]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setOpportunities([]);
      setIsLoading(false);
      return;
    }

    // Stale-while-revalidate: if we have cached data for this business, render it
    // instantly and refresh in the background (no blocking spinner).
    const hasCache = opportunitiesCache.has(businessId);
    if (hasCache) {
      setOpportunities(opportunitiesCache.get(businessId) ?? []);
      const cachedSettings = noActivitySettingsCache.get(businessId);
      if (cachedSettings) setNoActivitySettings(cachedSettings);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await refreshData();
    } catch (err) {
      console.error('Error loading CRM required actions:', err);
      setError('Failed to load lead actions.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, refreshData]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setOpportunities([]);
      return;
    }
    void load();
  }, [load, enabled]);

  useEffect(() => {
    if (!enabled || !noActivitySettings.enabled) return;
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, [enabled, noActivitySettings.enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;

    const scheduleRefresh = () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      refreshDebounceRef.current = setTimeout(() => {
        void refreshData().catch((err) =>
          console.error('Error refreshing CRM required actions:', err)
        );
      }, 400);
    };

    const channel = supabase
      .channel(`crm-required-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pipeline_opportunities',
          filter: `business_id=eq.${businessId}`,
        },
        scheduleRefresh
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'opportunity_activities',
          filter: `business_id=eq.${businessId}`,
        },
        scheduleRefresh
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, refreshData]);

  useEffect(() => {
    if (!enabled) return;

    const handleOpportunityReadOnActivity = (event: Event) => {
      const { opportunityIds } = (event as CustomEvent<{ opportunityIds: number[] }>).detail || {};
      if (!Array.isArray(opportunityIds) || opportunityIds.length === 0) return;

      const idSet = new Set(opportunityIds);
      setOpportunities((prev) =>
        prev.map((opp) => (idSet.has(opp.id) ? { ...opp, is_unread: false } : opp))
      );
      opportunityIds.forEach((opportunityId) => updateUnreadBadge(opportunityId));
    };

    window.addEventListener(CRM_OPPORTUNITY_READ_ON_ACTIVITY_EVENT, handleOpportunityReadOnActivity);
    return () => {
      window.removeEventListener(CRM_OPPORTUNITY_READ_ON_ACTIVITY_EVENT, handleOpportunityReadOnActivity);
    };
  }, [enabled, updateUnreadBadge]);

  const markOpportunityRead = useCallback(
    async (opportunityId: number) => {
      if (!supabase) return;

      const opportunity = opportunities.find((opp) => opp.id === opportunityId);
      if (opportunity?.is_unread) {
        updateUnreadBadge(opportunityId);
      }

      try {
        await persistOpportunityRead(supabase, opportunityId, businessId);
        setOpportunities((prev) =>
          prev.map((opp) => (opp.id === opportunityId ? { ...opp, is_unread: false } : opp))
        );
      } catch (readError) {
        console.error('Error marking opportunity as read:', readError);
      }
    },
    [supabase, businessId, opportunities, updateUnreadBadge]
  );

  const requiredActions = useMemo(
    () =>
      buildCrmRequiredActions(opportunities, {
        now,
        noActivity: noActivitySettings,
      }),
    [opportunities, now, noActivitySettings]
  );

  return {
    opportunities,
    requiredActions,
    isLoading,
    error,
    refresh: load,
    markOpportunityRead,
  };
}
